import { useState } from "react";
import { useNavigate } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { BottomCTA, TextField, TopNavigation } from "@hamlak/ui";
import { apiFetch, isApiError } from "@/api/client";
import { ep } from "@/api/endpoints";
import { qk } from "@/api/queryKeys";
import type { CouponRedeemRequest, CouponRedeemResponse } from "@/api/types";
import { t } from "@/i18n/ko";
import { useAuthStore } from "@/stores/authStore";
import { useUiStore } from "@/stores/uiStore";
import "./me.css";

/** 입력란 에러로 보여 줄 서버 코드(문구는 클라 소유, error.<CODE>). 그 외(429·네트워크 등)는 스낵바. */
const FIELD_ERRORS = ["COUPON_NOT_FOUND", "COUPON_DISABLED", "COUPON_NOT_STARTED", "COUPON_EXPIRED", "COUPON_ALREADY_REDEEMED", "COUPON_EXHAUSTED"] as const;
type FieldErrorCode = (typeof FIELD_ERRORS)[number];
const isFieldError = (c: string): c is FieldErrorCode => (FIELD_ERRORS as readonly string[]).includes(c);

/**
 * 쿠폰 등록 /me/coupon(F06-09 입력 · F06-10 완료 토스트 806:202). POST /me/coupons/redeem {code}.
 * 쿠폰 상태 에러는 입력란 에러, 429·네트워크는 스낵바. 성공하면 잔액을 반영하고 me·bricks 쿼리를 무효화한 뒤 마이페이지로 돌아가 완료 토스트를 띄운다(완료 풀페이지 없음, AC0-291).
 */
export function CouponPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const showSnackbar = useUiStore((s) => s.showSnackbar);
  const [code, setCode] = useState("");
  const [serverError, setServerError] = useState<string | undefined>();
  const [submitting, setSubmitting] = useState(false);

  const trimmed = code.trim();

  const back = () => {
    // 직접 진입(히스토리 없음)이면 /me 로
    if (typeof window !== "undefined" && (window.history.state as { idx?: number } | null)?.idx) navigate(-1);
    else navigate("/me", { replace: true });
  };

  const submit = async () => {
    if (!trimmed || submitting) return;
    setSubmitting(true);
    try {
      const res = await apiFetch<CouponRedeemResponse>(ep.meCouponRedeem, { method: "POST", body: { code: trimmed } satisfies CouponRedeemRequest });
      useAuthStore.getState().setBalance(res.balance);
      void queryClient.invalidateQueries({ queryKey: qk.me });
      void queryClient.invalidateQueries({ queryKey: qk.bricks });
      void queryClient.invalidateQueries({ queryKey: ["transactions"] });
      showSnackbar(t("coupon.toast", { amount: res.amount.toLocaleString("ko-KR") }), undefined, "positive");
      back();
    } catch (e) {
      if (isApiError(e) && isFieldError(e.code)) setServerError(t(`error.${e.code}`));
      else if (isApiError(e) && e.code === "RATE_LIMITED") showSnackbar(t("error.RATE_LIMITED"));
      else showSnackbar(isApiError(e) && e.code === "NETWORK" ? t("error.network") : t("error.generic"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="page auth" data-testid="coupon-page">
      <TopNavigation title={t("page.coupon")} right={null} onBack={back} />
      <div className="auth__form">
        <TextField
          label={t("coupon.label")}
          value={code}
          onValueChange={(v) => {
            setCode(v);
            setServerError(undefined);
          }}
          placeholder={t("coupon.placeholder")}
          maxLength={64}
          showCounter={false}
          {...(serverError ? { error: serverError } : {})}
          autoCapitalize="characters"
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
        />
        <ul className="coupon__notice">
          <li>· {t("coupon.notice.1")}</li>
          <li>· {t("coupon.notice.2")}</li>
          <li>· {t("coupon.notice.3")}</li>
        </ul>
      </div>
      <BottomCTA primary={{ children: t("coupon.submit"), disabled: !trimmed, loading: submitting, onClick: () => void submit() }} />
    </main>
  );
}
